import storeManager from '../store/StoreManager';
import responseReader from '../store/readHelpers/ResponseReader';
import scorableItemReader from '../store/readHelpers/ScorableItemReader';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import sessionReader from '../store/readHelpers/SessionReader';
import navigationService from './NavigationService';
import branchRuleReader from '../store/readHelpers/BranchRuleReader';
import apiReader from '../store/readHelpers/ApiReader';
import branchRuleService from './BranchRuleService';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import responseEvaluator from './ResponseEvaluator';
import responseActions from '../actions/ResponseActions';
import qtiConstants from '../qti/Constants';
import settingsReader from '../store/readHelpers/SettingsReader';
import questionReader from '../store/readHelpers/QuestionReader';
import sessionActions from '../actions/SessionActions';
import catDeliveryService from './CatDeliveryService';
import deliverService from './DeliveringService';
import responseService from './ResponseService';
import questionSelectionService from './QuestionSelectionService';

const footerService = {

    /**
     * Returns true when all sections in the assessment have been attempted
     * @param {array} assessment
     * @returns {boolean}
     */
    getAssessmentCompleted(assessment) {
        return !!assessment.sections?.every(section => this.getSectionAttempted(section));
    },

    /**
     * Returns true when all questions in the section have been attempted
     * @param {array} section
     * @returns {boolean}
     */
    getSectionAttempted(section, state = storeManager.getState()) {
        // Handle manual selection of questions
        if (questionSelectionService.sectionHasManualSelectionEnabled(section)) {
            const sectionId = section?.id;
            // Handle CSCB
            if (questionSelectionService.isCandidateSelectedContentBlockEnabledForSection(sectionId)) {
                const sectionsToPull = section?.numberOfSectionsSelected;

                if (sectionsToPull > 0) {
                    const selectedCount = this.getCountOfSectionsSelectedAndAttempted(section);
                    return selectedCount === sectionsToPull;
                }
                return false;
            }
            // Handle CSQ
            const questionsToPull = questionSelectionService.getNumberOfQuestionsToPullInSection(section);

            if (questionsToPull > 0) {
                const selectedCount = questionSelectionService.getSectionSelectedQuestions(section)?.length;
                return selectedCount === questionsToPull;
            }
        }
        return section.questions.every(question => this.isQuestionAttemptedWithAllScorableItems(question, state));
    },

    isQuestionAttemptedWithAllScorableItems(question, state = storeManager.getState()) {
        const contentRevisionId = question.contentRevisionId;
        const questionAttempted = responseReader.isQuestionAttempted(question, state);
        if (questionAttempted && scorableItemReader.useNumberPerInteraction()) {
            return scorableItemReader
                .getScorableItemsInQuestion(contentRevisionId)
                .every((item) => {
                    const itemResult = responseReader.getItemResultForQuestion(contentRevisionId);
                    return scorableItemNumberingService.isAttemptedScorableItem(question.contentRevisionId, item, itemResult);
                });
        }
        return questionAttempted;
    },

    getCountOfSectionsSelectedAndAttempted(inlineSection, state = storeManager.getState()) {
        let selectedSectionCount = 0;
        const sections = inlineSection?.sections;
        const questions = [];
        if (sections) {
            sections.forEach((section) => {
                const questionRevisionIds = questionSelectionService.getSectionSelectedQuestions(section);
                const questionCount = questionRevisionIds?.length;
                if (questionCount > 0) {
                    questionRevisionIds.forEach((questionRevisionId) => {
                        const question = assessmentReader.getQuestion(questionRevisionId);
                        questions.push(question);
                    });
                    if (questions.every(question => this.isQuestionAttemptedWithAllScorableItems(question, state))) {
                        selectedSectionCount++;
                    }
                }
            });
        }
        return selectedSectionCount;
    },

    shouldDoBranchRulesEvaluation() {
        return sessionReader.isContextExam()
            && navigationService.isOnLastQuestion()
            && branchRuleReader.hasBranchRules()
            && !branchRuleReader.hasEvaluatedBranchRules();
    },

    async gotoCatTestNextStep() {
        const store = storeManager.getStore();
        const state = storeManager.getState();
        if (questionReader.isAdaptiveThresholdStep()) {
            const currentQuestionId = state.currentlyShown.questionId;
            const currentSectionId = state.currentlyShown.sectionId;
            if (settingsReader.isCatSyncMode()) {
                store.dispatch(
                    sessionActions.setCatTriggerInProgress(true)
                );
                await catDeliveryService.requestAdaptiveQuestionSet(currentQuestionId, currentSectionId);
            } else {
                catDeliveryService.requestAdaptiveQuestionSetWhenReady(currentQuestionId, currentSectionId);
            }
        } else if (navigationService.isOnLastQuestion() && sessionReader.isAutoDeliverHighStakesEnabled()
            && responseReader.isQuestionDone(assessmentReader.getQuestion(state.currentlyShown.questionId))) {
            const assessmentRunId = sessionReader.getAssessmentRunId();
            navigationService.navigateToAutoSubmit();
            await deliverService.clientSideAutoDeliver(assessmentRunId);
        }
    },

    gotoNextTestStepAfterPendingResponses() {
        const interval = setInterval(
            () => {
                if (!apiReader.hasUnsavedUserResponses()) {
                    clearInterval(interval);
                    footerService.gotoNextTestQuestion();
                }
            }, 500);
        return false;
    },

    async gotoNextTestQuestion() {
        const state = storeManager.getState();
        if (this.shouldDoBranchRulesEvaluation()) {
            if (apiReader.isOnline() && apiReader.isServerOK()) {
                await branchRuleService.evaluateBranchRule();
            } else {
                return false;
            }
        }
        if (sessionReader.isContextReadinessCheck()) {
            const questionId = state.currentlyShown.questionId;
            const question = assessmentReader.getQuestion(questionId);
            const response = state.response[questionId];
            const correctResponses = state.correctResponses[questionId];
            const autoFeedbackName = responseEvaluator.evaluateQuestionResponses(question, response.responseIdentifiers, correctResponses);
            storeManager.dispatch(
                responseActions.setOutcomeVariables(questionId, autoFeedbackName)
            );
            if (autoFeedbackName === qtiConstants.autoFeedbackNames.CORRECT || autoFeedbackName === qtiConstants.autoFeedbackNames.ANSWERED) {
                return navigationService.navigateToNextStep();
            }
            return false;
        }
        return navigationService.navigateToNextStep();
    },

    async gotoNextTestStep() {
        const { currentlyShown: { questionId } } = storeManager.getState();
        const currentQuestion = assessmentReader.getQuestion(questionId);

        if (!navigationService.isNavigatingAwayFromIncompleteResponsesConfirmed(questionId)) {
            return;
        }

        if (settingsReader.isCatTestType()) {
            await this.gotoCatTestNextStep();
        }

        if (apiReader.hasUnsavedUserResponses() && this.shouldDoBranchRulesEvaluation()) {
            this.gotoNextTestStepAfterPendingResponses();
            return;
        }

        if (questionReader.shouldCommitAnswerTimestamp(currentQuestion)) {
            if (responseService.isOneWayNavigationDisallowedOfflineMode()) {
                storeManager.dispatch(responseActions.setOneWayNavigationOfflineCommitAttempt(true));
                return;
            }

            this.updateCommitAnswerStatus(currentQuestion);
            if (!questionReader.shouldAutocommitAnswer(currentQuestion)) {
                return;
            }
        }

        this.gotoNextTestQuestion();
    },

    updateCommitAnswerStatus(currentQuestion) {
        if (questionReader.shouldAutocommitAnswer(currentQuestion)) {
            // if question is attempted and autocommit enabled, then set 'pending' status
            responseService.setQuestionCommitAnswerPendingStatus(currentQuestion.contentRevisionId);
        } else {
            // otherwise set 'confirm' status
            storeManager.dispatch(responseActions.setConfirmQuestionCommitTimestamp(currentQuestion.contentRevisionId));
        }
    },

    hasRubricToDisplay(questionId) {
        const currentlyShownQuestion = assessmentReader.getQuestion(questionId);
        return settingsReader.isShowRubricForQuestionEnabled() && !!currentlyShownQuestion?.hasRubricsMarking;
    },

    /**
     * Handles key down events for question navigation within sections
     * @param {object} event - The keyboard event
     */
    handleOnKeyDownForQuestionNavigationInSections(event, activeElement = null) {
        const element = activeElement || document.activeElement;

        if (!Object.values(qtiConstants.SUPPORTED_NAV_KEYS_FOR_QUESTION_IN_SECTIONS).includes(event.key) || !element) {
            return;
        }

        event.preventDefault();

        const tablist = event.currentTarget;
        const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));

        let currentIndex = tabs.indexOf(element);
        if (currentIndex === -1) {
            currentIndex = tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
            if (currentIndex === -1) {
                currentIndex = 0;
            }
        }
        let nextIndex;
        switch (event.key) {
        case qtiConstants.SUPPORTED_NAV_KEYS_FOR_QUESTION_IN_SECTIONS.ARROW_LEFT:
            nextIndex = ((currentIndex - 1) + tabs.length) % tabs.length;
            break;
        case qtiConstants.SUPPORTED_NAV_KEYS_FOR_QUESTION_IN_SECTIONS.ARROW_RIGHT:
            nextIndex = (currentIndex + 1) % tabs.length;
            break;
        case qtiConstants.SUPPORTED_NAV_KEYS_FOR_QUESTION_IN_SECTIONS.HOME:
            nextIndex = 0;
            break;
        case qtiConstants.SUPPORTED_NAV_KEYS_FOR_QUESTION_IN_SECTIONS.END:
            nextIndex = tabs.length - 1;
            break;
        default:
            return;
        }

        if (nextIndex >= 0) {
            const nextTab = tabs[nextIndex];

            if (nextTab) {
                nextTab.focus();
            }
        }
    },
};

export default footerService;
